import React, { useEffect, useRef, useState } from 'react';
import { wikiAPI } from '../../api/api';
import './FilePreviewModal.css';

const FilePreviewModal = ({ file, onClose, onFileDeleted, canEdit }) => {
  const [loading, setLoading] = useState(true);
  const [previewSrc, setPreviewSrc] = useState('');
  const previewObjectUrlRef = useRef(null);

  const isImage = () => file.mime_type?.startsWith('image/');
  const isPDF = () =>
    file.mime_type?.includes('pdf') || file.original_name?.toLowerCase().endsWith('.pdf');
  const isText = () =>
    file.mime_type?.startsWith('text/') ||
    file.original_name?.match(/\.(txt|md|json|xml|html|css|js|ts|jsx|tsx|log|csv)$/i);

  const isOfficeFile = () =>
    file.mime_type?.includes('word') ||
    file.mime_type?.includes('excel') ||
    file.mime_type?.includes('powerpoint') ||
    file.original_name?.match(/\.(docx?|xlsx?|pptx?|odt|ods|odp)$/i);

  useEffect(() => {
    let active = true;

    const revokePreviewUrl = () => {
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
        previewObjectUrlRef.current = null;
      }
    };

    const loadPreview = async () => {
      if (!isImage() && !isPDF() && !isText()) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        revokePreviewUrl();

        const blob = await wikiAPI.previewFile(file.id);
        if (!active) return;

        const objectUrl = URL.createObjectURL(blob);
        previewObjectUrlRef.current = objectUrl;
        setPreviewSrc(objectUrl);
      } catch (error) {
        if (active) {
          setPreviewSrc('');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadPreview();

    return () => {
      active = false;
      revokePreviewUrl();
    };
  }, [file.id, file.mime_type, file.original_name]);

  const getFileIcon = () => {
    const mimeType = file.mime_type?.toLowerCase();
    const fileName = file.original_name?.toLowerCase();

    if (isPDF()) return 'fa-file-pdf';
    if (mimeType?.includes('word') || fileName?.match(/\.docx?$/)) return 'fa-file-word';
    if (mimeType?.includes('excel') || fileName?.match(/\.xlsx?$/)) return 'fa-file-excel';
    if (mimeType?.includes('powerpoint') || fileName?.match(/\.pptx?$/)) return 'fa-file-powerpoint';
    if (isImage()) return 'fa-file-image';
    if (isText()) return 'fa-file-alt';
    if (isOfficeFile()) return 'fa-file';
    return 'fa-file';
  };

  const getFileTypeName = () => {
    const mimeType = file.mime_type?.toLowerCase();
    const fileName = file.original_name?.toLowerCase();

    if (isPDF()) return 'PDF документ';
    if (mimeType?.includes('word') || fileName?.match(/\.docx?$/)) {
      return fileName?.endsWith('.docx') ? 'Word документ (DOCX)' : 'Word документ (DOC)';
    }
    if (mimeType?.includes('excel') || fileName?.match(/\.xlsx?$/)) {
      return fileName?.endsWith('.xlsx') ? 'Excel таблица (XLSX)' : 'Excel таблица (XLS)';
    }
    if (mimeType?.includes('powerpoint') || fileName?.match(/\.pptx?$/)) {
      return fileName?.endsWith('.pptx')
        ? 'PowerPoint презентация (PPTX)'
        : 'PowerPoint презентация (PPT)';
    }
    if (isImage()) return 'Изображение';
    if (isText()) return 'Текстовый файл';
    if (mimeType?.includes('archive') || fileName?.match(/\.(zip|rar|7z|tar|gz)$/i)) {
      return 'Архив';
    }
    if (isOfficeFile()) return 'Офисный документ';

    return 'Документ';
  };

  const handleDownload = async () => {
    try {
      const blob = await wikiAPI.downloadFile(file.id);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = objectUrl;
      link.download = decodeURIComponent(file.original_name || file.name);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => {
        URL.revokeObjectURL(objectUrl);
      }, 0);
    } catch (error) {
      alert('Ошибка при скачивании файла');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Удалить файл "${file.original_name || file.name}"?`)) return;

    try {
      await wikiAPI.deleteFile(file.id);
      onFileDeleted(file.id);
      onClose();
    } catch (error) {
      alert('Ошибка при удалении файла');
    }
  };

  const getOfficeOnlineUrl = () => {
    const ext = file.original_name?.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'doc':
      case 'docx':
        return 'https://www.office.com/launch/word';
      case 'xls':
      case 'xlsx':
        return 'https://www.office.com/launch/excel';
      case 'ppt':
      case 'pptx':
        return 'https://www.office.com/launch/powerpoint';
      default:
        return 'https://www.microsoft.com/ru-ru/microsoft-365/free-office-online-for-the-web';
    }
  };

  const openOfficeOnline = () => {
    window.open(getOfficeOnlineUrl(), '_blank', 'noopener,noreferrer');
  };

  const renderLoading = () => (
    <div className="preview-loading">
      <div className="spinner"></div>
      <p>Подготовка документа...</p>
    </div>
  );

  const renderImagePreview = () => {
    if (loading) return renderLoading();

    return previewSrc ? (
      <div className="image-preview-container">
        <img
          src={previewSrc}
          alt={file.original_name || file.name}
          className="preview-image"
        />
      </div>
    ) : (
      renderUnknownPreview()
    );
  };

  const renderPDFPreview = () => {
    if (loading) return renderLoading();

    return previewSrc ? (
      <div className="pdf-preview-container">
        <iframe
          src={`${previewSrc}#toolbar=0&navpanes=0&scrollbar=1`}
          className="preview-iframe"
          title={file.original_name || file.name}
        />
      </div>
    ) : (
      renderUnknownPreview()
    );
  };

  const renderTextPreview = () => {
    if (loading) return renderLoading();

    return previewSrc ? (
      <div className="text-preview-container">
        <iframe
          src={previewSrc}
          className="preview-iframe"
          title={file.original_name || file.name}
        />
      </div>
    ) : (
      renderUnknownPreview()
    );
  };

  const renderWordPreview = () => {
    const fileType = getFileTypeName();
    const isExcel = fileType.includes('Excel');
    const isPowerPoint = fileType.includes('PowerPoint');

    let iconColor = '#2b579a';
    let iconClass = 'fa-file-word';
    let solutions = [
      { name: 'Microsoft Word', desc: 'Стандартная программа' },
      { name: 'Google Docs', desc: 'Загрузите файл на Google Drive' },
      { name: 'LibreOffice', desc: 'Бесплатная альтернатива' },
      { name: 'Word Online', desc: 'Веб-версия от Microsoft' }
    ];

    if (isExcel) {
      iconColor = '#217346';
      iconClass = 'fa-file-excel';
      solutions = [
        { name: 'Microsoft Excel', desc: 'Стандартная программа' },
        { name: 'Google Sheets', desc: 'Загрузите файл на Google Drive' },
        { name: 'LibreOffice Calc', desc: 'Бесплатная альтернатива' },
        { name: 'Excel Online', desc: 'Веб-версия от Microsoft' }
      ];
    } else if (isPowerPoint) {
      iconColor = '#d24726';
      iconClass = 'fa-file-powerpoint';
      solutions = [
        { name: 'Microsoft PowerPoint', desc: 'Стандартная программа' },
        { name: 'Google Slides', desc: 'Загрузите файл на Google Drive' },
        { name: 'LibreOffice Impress', desc: 'Бесплатная альтернатива' },
        { name: 'PowerPoint Online', desc: 'Веб-версия от Microsoft' }
      ];
    }

    return (
      <div className="office-preview-container">
        <i className={`fas ${iconClass} office-icon`} style={{ color: iconColor }}></i>
        <h3 className="office-title">{fileType}</h3>
        <p className="office-description">
          Для просмотра файла скачайте его и откройте в соответствующей программе
        </p>

        <div className="office-actions">
          <button className="exchange-btn primary-btn" onClick={handleDownload}>
            <i className="fas fa-download"></i> Скачать файл
          </button>
          <button className="exchange-btn secondary-btn" onClick={openOfficeOnline}>
            <i className="fas fa-external-link-alt"></i> Открыть Office Online
          </button>
        </div>

        <div className="office-solutions">
          <h5>Способы открытия {isExcel ? 'Excel' : isPowerPoint ? 'PowerPoint' : 'Word'} файлов:</h5>
          <div className="solutions-list">
            {solutions.map((solution, index) => (
              <div key={index} className="solution-item">
                <p>• <strong>{solution.name}</strong> - {solution.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderArchivePreview = () => (
    <div className="office-preview-container">
      <i className="fas fa-file-archive office-icon" style={{ color: '#6c757d' }}></i>
      <h3 className="office-title">Архивный файл</h3>
      <p className="office-description">
        Для просмотра содержимого архива скачайте его и распакуйте с помощью архиватора
      </p>

      <div className="office-actions">
        <button className="exchange-btn primary-btn" onClick={handleDownload}>
          <i className="fas fa-download"></i> Скачать архив
        </button>
      </div>
    </div>
  );

  const renderUnknownPreview = () => (
    <div className="office-preview-container">
      <i className="fas fa-file office-icon" style={{ color: '#6c757d' }}></i>
      <h3 className="office-title">{getFileTypeName()}</h3>
      <p className="office-description">
        Неизвестный тип файла. Вы можете скачать его для просмотра
      </p>

      <div className="office-actions">
        <button className="exchange-btn primary-btn" onClick={handleDownload}>
          <i className="fas fa-download"></i> Скачать файл
        </button>
      </div>
    </div>
  );

  const renderPreview = () => {
    if (isImage()) return renderImagePreview();
    if (isPDF()) return renderPDFPreview();
    if (isText()) return renderTextPreview();
    if (isOfficeFile()) return renderWordPreview();

    if (file.mime_type?.includes('archive') || file.original_name?.match(/\.(zip|rar|7z|tar|gz)$/i)) {
      return renderArchivePreview();
    }

    return renderUnknownPreview();
  };

  return (
    <div className="modal-overlay">
      <div className="centered-modal file-preview-modal">
        <div className="preview-header">
          <h3 title={decodeURIComponent(file.original_name || file.name)}>
            <i className={`fas ${getFileIcon()}`}></i>
            {decodeURIComponent(file.original_name || file.name)}
          </h3>
          <div className="preview-actions">
            <button className="close" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        <div className="preview-content">
          {renderPreview()}
        </div>

        <div className="preview-footer">
          <div className="file-info">
            <span>
              <i className="fas fa-hdd"></i>
              Размер: {file.file_size ? `${(file.file_size / 1024 / 1024).toFixed(2)} MB` : 'Неизвестно'}
            </span>
            <span>
              <i className="fas fa-calendar"></i>
              Загружен: {new Date(file.created_at).toLocaleDateString('ru-RU')}
            </span>
            <span>
              <i className="fas fa-file"></i>
              Тип: {getFileTypeName()}
            </span>
            {file.uploaded_by_username && (
              <span>
                <i className="fas fa-user"></i>
                Пользователь: {file.uploaded_by_username}
              </span>
            )}
          </div>

          <div className="preview-actions">
            {canEdit && (
              <button className="btn-delete" onClick={handleDelete}>
                <i className="fas fa-trash"></i>
                Удалить
              </button>
            )}

            <button className="exchange-btn" onClick={handleDownload}>
              <i className="fas fa-download"></i>
              Скачать
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilePreviewModal;
